import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Card, Col, Row } from 'react-bootstrap'
import { MenuItem } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import PromotionApi from 'api/application/promotion.api'
import { routeName } from 'routes/routes-name'
import { fileUrl } from 'api/api'

/** UTILS || SLICE */
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'
import { toBuddhistYear } from 'utils/app.utils'
import { onImgError } from 'constants/images'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextSearch from 'component/Input/InputTextSearch'
import FilterSelect from 'component/Select/FilterSelect'
import ButtonCustom from 'component/Button/ButtonCustom'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { swalActive } from 'component/Alert/Swal'
import { notiSuccess, notiError } from 'component/notifications/notifications'

/** STYLE */
import * as UseStyled from 'features/application/promotions/useStyled'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

export default function Promotions() {
  const { t } = useTranslation()
  const history = useHistory()

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [promotions, setPromotions] = useState([]);

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status } //fix filter status all
    const resp = await PromotionApi.findAll(condition)
    if (resp.status === 200) {
      setRowCount(resp.headers['x-total'])
      setPromotions(resp.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const onActive = () => {
    if (id) {
      PromotionApi.updatePromotionStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PROMOTIONS.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PROMOTIONS.MESSAGE.ERROR.DEFAULT'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      PromotionApi.removePromotion(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('PROMOTIONS.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('PROMOTIONS.MESSAGE.ERROR.DEFAULT'))
        }
      })
    }
  }

  const onCreate = () => {
    history.push(routeName.promotions + '/create')
  }

  const onEdit = (id: any) => {
    history.push(routeName.promotions + '/edit', { promotionId: id })
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#', width: '50px' },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.IMAGE'), width: '150px' },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.TITLE') },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.DATE_START'), width: '160px' },
    { id: 'message', disablePadding: false, label: t('PROMOTIONS.TABLE.DATE_END'), width: '160px' },
    { id: 'status', disablePadding: false, label: t('PROMOTIONS.TABLE.STATUS'), width: '150px' },
    { id: 'updatedBy', disablePadding: false, label: t('PROMOTIONS.TABLE.UPDATED_BY'), width: '180px' },
    { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { promotionId, image, subject, datePublic, timePublic, dateEnd, timeEnd, status: statusPromotions, updatedBy, updatedAt } = objData
    const statusBtnActive = statusPromotions === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = statusPromotions === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: promotionId,
      id: promotionId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        {
          option: 'COMPONENT', align: 'left', component: (
            <UseStyled.ImageWrap>
              {
                image?.filePath && image?.fileName && (
                  <img src={`${fileUrl}/${image?.filePath}/${image?.fileName}`} onError={onImgError} className="w-100" alt='' />
                )
              }
            </UseStyled.ImageWrap>
          )
        },
        { option: 'TEXT', align: 'left', label: subject },
        { option: 'TEXT', align: 'left', label: datePublic && datePublic !== '0000-00-00' && timePublic ? toBuddhistYear(moment(`${datePublic} ${timePublic}`), 'DD/MM/YYYY HH.mm') : '-' },
        { option: 'TEXT', align: 'left', label: dateEnd && dateEnd !== '0000-00-00' && timeEnd ? toBuddhistYear(moment(`${dateEnd} ${timeEnd}`), 'DD/MM/YYYY HH.mm') : '-' },
        { option: 'STATUS', align: 'center', label: statusPromotions },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }

    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.id)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }
  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.subject)
  }

  return (
    <Card className={'border-0 h-100 overflow-auto'}>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`PROMOTIONS.MESSAGE.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`PROMOTIONS.MESSAGE.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { subject: name, status: t(`PROMOTIONS.MESSAGE.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 132px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('PROMOTIONS.HEADER')} />
      <Card.Body>
        <Card.Title className={'mb-0 pl-xl-3'}>
          <Row>
            <Col md={8}>
              <Row>
                <Col sm={12} md={6} xl={4} className={'pt-2'}>
                  <InputTextSearch
                    keyInput={'search'}
                    label={t('PROMOTIONS.SEARCH')}
                    value={search}
                    onchange={(event) => {
                      setSearch(event.target.value)
                      setPage(1)
                    }}
                    onClear={(event) => {
                      setSearch('')
                      setPage(1)
                    }}
                  />
                </Col>
                <Col sm={12} md={6} lg={4} className={'pl-md-0 pt-2'}>
                  <FilterSelect
                    onchange={(event) => {
                      const value = event.target.value
                      if (value) {
                        handleChangeStatus(value)
                        setPage(1)
                      }
                    }}
                    renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                    label={t('STATUS.LABEL')}
                    selectId="select-status"
                    value={status}
                    labelId="label-status"
                    options={[
                      <MenuItem key="1" value="ALL">
                        {t('STATUS.ALL')}
                      </MenuItem>,
                      <MenuItem key="2" value="ACTIVE">
                        {t('STATUS.ACTIVE')}
                      </MenuItem>,
                      <MenuItem key="3" value="INACTIVE">
                        {t('STATUS.INACTIVE')}
                      </MenuItem>
                    ]}
                  />
                </Col>
              </Row>
            </Col>
            <Col md={4} className="mt-2 mt-md-auto">
              <div className="pr-xl-2">
                <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={onCreate} textButton={t('PROMOTIONS.BUTTON.ADD_PROMOTIONS')} className="w-auto ml-auto d-flex mt-auto" />
              </div>
            </Col>
          </Row>
        </Card.Title>

        <UseStyled.TableResponsive className={'mt-3 pl-xl-3 pr-xl-2'}>
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            rowCount={rowCount}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            headCells={headCells}
            rowsData={(
              _.map(promotions, (promotion, index: number) => {
                return renderData(promotion, index)
              })
            )}
            tableFixedWidth
            tableMinWidth={1200}
          />
        </UseStyled.TableResponsive>
      </Card.Body>
    </Card>
  )
}
